import React, { useState } from "react";
import styled from "styled-components";
import EditIcon from "@mui/icons-material/Edit";
import Modal from "common/Modal";
import { checkNothingPending, capitalize } from "common/api";
import { isPolicyNameValid } from "../Rules/utils";
import { useSearchParams } from "react-router-dom";

const EditNameButton = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 20px 5px 5px;
  margin: 0 0px 0 10px;
  svg {
    margin-right: 5px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const NameWrapper = styled.span`
  font-family: "AllianceNo2-SemiBold" !important;
  font-weight: 100;
  padding: 0 10px;
`;

export const parsePolicyFlowLines = ({ response, name, type }) => {
  const lines = response.trim().split("\n");
  const { policy } = lines.reduce(
    (acc, line) => {
      if (line === `policy ${type} ${name}`) {
        acc.inPolicy = true;
      } else if (acc.inPolicy && line.match(/^\s+/)) {
        acc.policy.settings.push(line);
      } else {
        acc.inPolicy = false;
        if (
          line.startsWith(`rule ${type} `) &&
          line.endsWith(` policy ${name}`)
        ) {
          acc.policy.rules.push(line);
        }
      }
      return acc;
    },
    {
      inPolicy: false,
      policy: { name, settings: [], rules: [], type },
    },
  );

  return { policy };
};

export const showConfig = (nameInit, type) => {
  const promises = [ifCl.run("show config running all")];
  if (type === "rate") {
    promises.push(ifCl.run("show api subscriber-group"));
  }

  return Promise.all(promises).then(([response, apiResponse]) => {
    const parsed = parsePolicyFlowLines({ response, name: nameInit, type });
    if (apiResponse) {
      parsed.subscriberGroups = getSubscriberGroups(apiResponse, nameInit);
    }
    return parsed;
  });
};

const getSettingIndent = (setting) => {
  const match = setting.match(/^\s*/);
  return match ? match[0].length : 0;
};

export const updatePolicy = ({
  name,
  nameNew,
  type,
  settings,
  rules,
  subscriberGroups,
}) => {
  const cmdsRules =
    rules.length > 0
      ? rules.map((rule) => {
          const index = rule.lastIndexOf(" policy " + name);
          const newRule =
            index !== -1
              ? rule.substring(0, index) + " policy " + nameNew
              : rule;
          return "no " + rule + "\n" + newRule;
        })
      : [];
  const cmdsSettings = settings.reduce(
    (acc, setting) => {
      const settingIndent = getSettingIndent(setting);
      while (acc.indent > settingIndent) {
        acc.cmds.push("exit");
        acc.indent -= 2;
      }
      acc.cmds.push(setting.trim());
      acc.indent = settingIndent;
      return acc;
    },
    { cmds: [], indent: 0 },
  );

  let cmds = [
    `configure`,
    `no policy ${type} ${name}`,
    `policy ${type} ${nameNew}`,
    ...(cmdsSettings.cmds.length > 0 ? cmdsSettings.cmds : []),
    ...Array(cmdsSettings.indent / 2 === 0 ? 1 : cmdsSettings.indent / 2).fill("exit"),
    ...(rules.length > 0 ? cmdsRules : []),
    `commit`,
  ];

  return ifCl.run(`${cmds.join("\n")}\n`, /*is batch*/ true).then((response) => ({
    response,
    subscriberGroups,
  }));
};

export const NameModal = ({
  onClose,
  name: nameInit,
  type,
  policies,
  returnView,
  tab,
}) => {
  const [open, setOpen] = useState(true);
  const [name, setName] = useState(nameInit);
  const [errorFooter, setErrorFooter] = useState(null);
  const [searchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams.entries());

  if (!open) {
    return null;
  }

  const handleApply = () => {
    if (!name) {
      setErrorFooter("Empty name");
      return;
    }
    if (!isPolicyNameValid(name)) {
      setErrorFooter("Invalid name");
      return;
    }
    if (policies[name] !== undefined) {
      setErrorFooter("Duplicated policy name");
      return;
    }
    checkNothingPending()
      .then(() => {
        return showConfig(nameInit, type);
      })
      .then((response) => {
        const { policy, subscriberGroups } = response;
        return updatePolicy({
          name: nameInit,
          nameNew: name,
          type,
          settings: policy.settings,
          rules: policy.rules,
          subscriberGroups,
        })
          .then((response) => {
            const { subscriberGroups } = response;
            if (subscriberGroups && subscriberGroups.length > 0) {
              const cmdsSubscriberGroups = subscriberGroups.map(
                (subscriberGroup) => {
                  return `api subscriber-group ${subscriberGroup} shaping policy ${name}`;
                },
              );
              return ifCl.run(
                `${cmdsSubscriberGroups.join("\n")}\n`,
                /*is batch*/ true,
              );
            }
            return Promise.resolve();
          })
          .then((response) => {
            if (response) {
              return ifCl.run(
                "configure\nclear config changes\n",
                /*is batch*/ true,
              );
            } else {
              return Promise.resolve();
            }
          });
      })
      .then(() => {
        onClose();
        if (returnView === "viewStatusSubscriberGroups") {
          const { policy: _p, returnView: _rv, ...restParams } = params;
          const searchParams =
            Object.keys(restParams).length > 0 ? restParams : undefined;
          globalNavigate(returnView, {
            policy: name,
            ...searchParams,
            nameChanged: true,
          });
        } else if (returnView) {
          globalNavigate(`viewPolicy${capitalize(type)}Edit`, {
            returnView,
            policy: name,
            ...(tab ? { tab } : {}),
          });
        } else {
          globalNavigateBack();
        }
      })
      .catch((error) => {
        ifCl.run("configure\nclear config changes\n", /*is batch*/ true);
        setErrorFooter(error.message);
      });
  };

  return (
    <Modal
      title="Edit Policy Name"
      superIcon="edit"
      onClose={onClose}
      closeLabel="CANCEL"
      content={() => (
        <div className="input-group">
          <span className="input-group-addon" title="Policy name">
            Name
          </span>
          <input
            id="poliName"
            type="text"
            size="55"
            style={{ borderBottom: "1px solid #ddd" }}
            className="align-left"
            onChange={({ target }) => {
              setErrorFooter(null);
              setName(target.value);
            }}
            value={name}
          />
        </div>
      )}
      onApply={handleApply}
      applyDisabled={nameInit === name}
      applyLabel="OK"
      footerMessage={
        errorFooter ? (
          <span className="modal-err-msg color-red align-left">
            <strong>Error:</strong> {errorFooter}
          </span>
        ) : null
      }
    />
  );
};

function getSubscriberGroups(apiResponse, nameInit) {
  const lines = apiResponse.trim().split("\n");
  if (lines.length > 0) {
    const headers = lines[0].trim().split(/\s+/);
    const groupIdx = headers.indexOf("SUBSCRIBER-GROUP");
    const policyIdx = headers.indexOf("POLICY-RATE");
    const subscriberGroups =
      groupIdx !== -1 && policyIdx !== -1
        ? lines.slice(1).reduce((acc, line) => {
            const cols = line.trim().split(/\s+/);
            if (cols[policyIdx] === nameInit) {
              acc.push(cols[groupIdx]);
            }
            return acc;
          }, [])
        : [];
    return subscriberGroups;
  }
  return [];
}

function PolicyName({ name, type, policies, returnView, tab }) {
  const [displayNameModal, setDisplayNameModal] = useState(false);
  return (
    <Wrapper>
      <div>
        Name: <NameWrapper>{name}</NameWrapper>
      </div>
      <EditNameButton
        onClick={() => setDisplayNameModal(true)}
        className="btn btn-link waves-effect"
      >
        <EditIcon />
        Edit Name
      </EditNameButton>
      {displayNameModal ? (
        <NameModal
          type={type}
          name={name}
          onClose={() => setDisplayNameModal(false)}
          policies={policies}
          returnView={returnView}
          tab={tab}
        />
      ) : null}
    </Wrapper>
  );
}

export default PolicyName;
